import {
  ChartOptions,
  ChartData,
  Plugin,
  Chart as ChartJS,
  Tooltip,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from 'chart.js';

import { Skill } from './ArticleDetailStatisticsChart.types';

import { PATH } from '@/shared/constants';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip);

export const getChartOptions = (
  isMobile: boolean,
  firstSkill: Skill,
  secondSkill: Skill,
  thirdSkill: Skill | null
): ChartOptions<'bar'> & { barThickness: number } => ({
  maintainAspectRatio: false,
  indexAxis: 'y',
  barThickness: 14,
  scales: {
    y: {
      stacked: true,
      grid: {
        display: false,
        lineWidth: 0,
      },
      ticks: {
        crossAlign: 'far',
        color: ['#212121', '#757575', '#757575'],
        callback: function (value, index) {
          const skills = thirdSkill
            ? [firstSkill, secondSkill, thirdSkill]
            : [firstSkill, secondSkill];
          let roleName = skills[index].name;
          if (isMobile && roleName.length > 9) {
            roleName = roleName.substring(0, 9) + '...';
          } else if (roleName.length > 11) {
            roleName = roleName.substring(0, 11) + '...';
          }
          return roleName;
        },
      },
      afterFit: (axis) => {
        if (isMobile) {
          axis.width = 100;
          axis.paddingLeft = 122;
        } else {
          axis.width = 120;
          axis.paddingLeft = 142;
        }
        axis.paddingTop = 2;
        axis.paddingBottom = 0;
      },
    },
    x: {
      stacked: true,
      ticks: {
        callback: function (value) {
          return `${value}%`;
        },
        stepSize: isMobile ? 50 : 20,
        color: '#757575',
      },
      afterFit: (axis) => {
        axis.paddingTop = 0;
        axis.paddingBottom = 0;
      },
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      displayColors: false,
      padding: {
        x: 12,
        y: 6,
      },
      cornerRadius: 8,
      callbacks: {
        title: () => '',
        label: (tooltipItem) => {
          return `${tooltipItem.dataset.label} : ${tooltipItem.raw}%`;
        },
      },
    },
  },
});

export const getChartData = (
  firstSkill: Skill,
  secondSkill: Skill,
  thirdSkill: Skill | null
): ChartData<'bar'> => ({
  labels: thirdSkill
    ? [firstSkill.name, secondSkill.name, thirdSkill.name]
    : [firstSkill.name, secondSkill.name],
  datasets: [
    {
      label: '1년 이하',
      data: [
        firstSkill.viewCount.between0And1,
        secondSkill.viewCount.between0And1,
        thirdSkill?.viewCount.between0And1 ?? 0,
      ],
      borderColor: 'rgba(255, 255, 255, 0)',
      backgroundColor: [
        `rgba(134, 70, 242, 1)`,
        `rgba(134, 70, 242, 0.3)`,
        `rgba(134, 70, 242, 0.3)`,
      ],
      hoverBackgroundColor: `rgba(134, 70, 242, 1)`,
      borderWidth: 2,
    },
    {
      label: '2~4년',
      data: [
        firstSkill.viewCount.between2And4,
        secondSkill.viewCount.between2And4,
        thirdSkill?.viewCount.between2And4 ?? 0,
      ],
      borderColor: 'rgba(255, 255, 255, 0)',
      backgroundColor: [
        `rgba(160, 84, 232, 1)`,
        `rgba(160, 84, 232, 0.3)`,
        `rgba(160, 84, 232, 0.3)`,
      ],
      hoverBackgroundColor: `rgba(160, 84, 232, 1)`,
      borderWidth: 2,
    },
    {
      label: '5~7년',
      data: [
        firstSkill.viewCount.between5And7,
        secondSkill.viewCount.between5And7,
        thirdSkill?.viewCount.between5And7 ?? 0,
      ],
      borderColor: `rgba(255, 255, 255, 0)`,
      backgroundColor: [
        `rgba(69, 192, 179, 1)`,
        `rgba(69, 192, 179, 0.3)`,
        `rgba(69, 192, 179, 0.3)`,
      ],
      hoverBackgroundColor: `rgba(69, 192, 179, 1)`,
      borderWidth: 2,
    },
    {
      label: '8년 이상',
      data: [
        firstSkill.viewCount.between8And20,
        secondSkill.viewCount.between8And20,
        thirdSkill?.viewCount.between8And20 ?? 0,
      ],
      borderColor: `rgba(255, 255, 255, 0)`,
      backgroundColor: [
        `rgba(255, 218, 106, 1)`,
        `rgba(255, 218, 106, 0.3)`,
        `rgba(255, 218, 106, 0.3)`,
      ],
      hoverBackgroundColor: `rgba(255, 218, 106, 1)`,
      borderWidth: 2,
    },
  ],
});

const chartImages = [
  `${PATH.MEDIA_YOZM_IMAGE}/first.svg`,
  `${PATH.MEDIA_YOZM_IMAGE}/second.svg`,
  `${PATH.MEDIA_YOZM_IMAGE}/third.svg`,
].map((src) => {
  if (typeof window !== 'undefined') {
    const image = new Image();
    image.src = src;
    return image;
  }
  return null; // 서버에서는 이미지를 로드하지 않음
});

export const chartPlugins: Plugin[] = [
  {
    id: 'chart-image-plugin',
    beforeDraw: (chart) => {
      const ctx = chart.ctx;
      const yAxis = chart.scales['y'];
      yAxis.ticks.forEach((value, index) => {
        const image = chartImages[index];
        if (image !== null) {
          const y = yAxis.getPixelForTick(index);
          ctx.drawImage(image, 0, y - 9);
        }
      });
    },
  },
];
